import * as React from 'react';
import { Image } from 'antd';
import { ResolvedImageInput, useResolvedImageSrc } from '../../hooks/useResolvedImageSrc';
import ConvertingImagePlaceholder from '../converting_image_placeholder/ConvertingImagePlaceholder';

interface ResolvedImageProps extends ResolvedImageInput {
	width?: number | string;
	height?: number | string;
	style?: React.CSSProperties;
	className?: string;
	/** Set false where the image is decorative or the parent owns its own lightbox. */
	preview?: boolean;
}

/**
 * Render a stored image without the caller having to know HEIC exists.
 *
 * Prefer this over building a URL and handing it to `<img>`/antd `<Image>`: those render a broken
 * icon for an iPhone photo, and for a photo whose conversion is still in flight they render one
 * even though a perfectly good JPEG is seconds away. This shows a "Converting…" placeholder for
 * exactly that window and swaps the derivative in when it lands — on create forms too, where
 * there is no entity yet to carry `convertedImages`.
 */
export const ResolvedImage: React.FC<ResolvedImageProps> = ({
	url,
	fileId,
	s3ObjectKey,
	convertedImages,
	width,
	height,
	style,
	className,
	preview = true,
}) => {
	const resolved = useResolvedImageSrc({ url, fileId, s3ObjectKey, convertedImages });

	if (resolved.isConverting) {
		return (
			<div className={className} style={{ width, height, ...style }}>
				<ConvertingImagePlaceholder />
			</div>
		);
	}

	return (
		<Image
			src={resolved.src}
			className={className}
			style={{ width, height, objectFit: 'cover', ...style }}
			{...(preview && resolved.previewSrc && resolved.previewSrc !== resolved.src
				? { preview: { src: resolved.previewSrc } }
				: { preview })}
		/>
	);
};

export default ResolvedImage;
